import React, { useEffect, useState } from "react";
import { usePageMetadata } from "../context/PageMetadataContext";
import { horizon } from "../api/horizon";
import { Card, CardContent } from "../components/Card";
import { socialchain } from "../api/socialchain";
import TradingViewWidget from "../components/TradingViewWidget";
import { okx } from "../api/okx";
import { Link } from "react-router-dom";
import { timelib } from "../utils/time";
import { Table } from "react-bootstrap";
import OperationText from "../components/OperationText";
import CopyIcon from "../components/CopyIcon";
import AccountLabel from "../components/AccountLabel";
// import AdUnit from "../components/AdUnit";
import { useLanguage } from "../context/LanguageContext";
import AdSenseUnit from "../components/AdsenseUnit";
import AdUnit from "../components/AdUnit";

const HomePage: React.FC = () => {
  const { setHeading, setTitle, setDescription } = usePageMetadata();
  const { t, language } = useLanguage(); // Add language hook

  const [piSupply, setPiSupply] = useState({ circulating_supply: 6600486452, total_locked: 4968062696, total_supply: 10154594543 });
  interface CexBalance {
    name: string;
    balance: number;
    logo: string;
  }
  const [totalCexBalance, setTotalCexBalance] = useState(0);
  const [cexBalances, setCexBalances] = useState<CexBalance[]>([]);
  const [latestBlock, setLatestBlock] = useState(0);
  const [tps, setTps] = useState(0);
  const [operations, setOperations] = useState<any[]>([]);
  const [marketStats, setMarketStats] = useState({ price: 2, high24h: 2,open24h:2,change:0.0});
  const [piPriceUSD, setPiPriceUSD] = useState(0.0);
  interface Transaction {
    hash: string;
    created_at: string;
    ledger: number;
    operation_count: number;
  }

  const [latestTransactions, setLatestTransactions] = useState<Transaction[]>([]);

  useEffect(() => {
    setHeading(t('home.heading'));
    setTitle(t('home.title'));
    setDescription(t('home.description'));
  }, [setTitle, setDescription, setHeading, t, language]);

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = () => {
    try {
      fecthLatestBlock();
      fecthTps();
      fectLatestTransactions();
      fectLatestOperations();
      fecthMarketData();
      fecthCexBalances();
      fectSupply();
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };
  
  const fectSupply = () => {
    socialchain.getSupply()
      .then(dataSupply => {
        // Check if dataSupply is valid or use default values
        if (!dataSupply || typeof dataSupply !== 'object') {
          setPiSupply({ 
            circulating_supply: 6600486452, 
            total_locked: 4968062696, 
            total_supply: 10154594543 
          });
        } else {
          setPiSupply(dataSupply);
        }
      })
      .catch(() => {
        // Use default values on error
        setPiSupply({ 
          circulating_supply: 6600486452, 
          total_locked: 4968062696, 
          total_supply: 10154594543 
        });
      });
  }
  
  const fecthLatestBlock = () => {
    horizon.getLatestBlock().then(dataLatestBlock=>setLatestBlock(dataLatestBlock))
  }
  
  const fecthTps = () => {
    horizon.getTps().then(dataTps=>setTps(dataTps))
  }
  
  const fecthCexBalances = () => {
    horizon.getCexData().then(dataCexs=>{
      //get 2 first cex balances and set to cexBalances
      setCexBalances(dataCexs.slice(0,2).map((cex) => ({ name: cex.Name, balance: cex.Balance, logo: cex.Logo })))
      //get total cex balance and set to totalCexBalance
      setTotalCexBalance(dataCexs.reduce((acc, cur) => acc + cur.Balance, 0))
    })
  }
  
  const fectLatestTransactions = () => {
    // Latest Transactions
    horizon.getLatestTransactions("",10).then((txData) => {
      setLatestTransactions(txData._embedded.records);
    });
  }
  
  const fectLatestOperations = () => {
    horizon.getLatestOperations("",20).then((opData) => {
      setOperations(opData._embedded.records);
    })
  }
  
  var firstTimeFetchMarketData=true;
  const fecthMarketData = () => {
    okx.getMarketData()
      .then(marketData => {
        try {
          setMarketStats({
            price: parseFloat(marketData.idxPx || '2.0'), 
            high24h: parseFloat(marketData.high24h || '2.0'),
            open24h: parseFloat(marketData.open24h || '2.0'),
            change: ((parseFloat(marketData.idxPx || '2.0') - parseFloat(marketData.open24h || '2.0')) / 
                    parseFloat(marketData.open24h || '1.0')) * 100
          });
          if (firstTimeFetchMarketData) {
            setPiPriceUSD(parseFloat(marketData.idxPx || '2.0'));
            firstTimeFetchMarketData = false;
          }
        } catch (err) {
          console.error('Error processing market data:', err);
          setMarketStats({ price: 2, high24h: 2, open24h: 2, change: 0.0 });
        }
      })
      .catch(() => {
        setMarketStats({ price: 2, high24h: 2, open24h: 2, change: 0.0 });
      });
  }
  
  // Format number according to user locale
  const formatNumber = (value: number): string => {
    return value.toLocaleString(language, {  
      minimumFractionDigits: 0,  
      maximumFractionDigits: 3 
    });
  };
  
  // Format currency according to user locale
  const formatCurrency = (value: number): string => {
    return value.toLocaleString(language, {  
      minimumFractionDigits: 0,  
      maximumFractionDigits: 2 
    });
  };
  
  //fetch new price every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fecthMarketData();
      fectLatestTransactions();
      fecthLatestBlock();
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="">
      {/* Row 1: Summary Stats */}
      <div className="row g-3 mb-4">
        {/* Market Stats */}
        <div className="col-md-3">
          <Card>
            <CardContent>
              <h5 className="card-title">{t('home.market_cap')}</h5>
              <p className="strong-number">
                ${formatNumber(marketStats.price * piSupply.circulating_supply)}
              </p>
              <div className="grey-zone">
                <div className="mt-2">
                  <p className="neutral-color-5">{t('home.price')}</p>
                  <p className="strong-number">
                    ${formatNumber(marketStats.price)}
                  </p>
                  <hr className="neutral-color-5" />
                  <p className="neutral-color-5">{t('home.24h_change')}</p>
                  <p className="strong-number">
                    <span className={marketStats.change > 0 ? "text-success" : "text-danger"}>
                      {formatNumber(marketStats.change)}%
                    </span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Pi Supply */}
        <div className="col-md-3">
          <Card>
            <CardContent>
              <h5 className="card-title">{t('home.pi_supply')}</h5>
              <p className="strong-number">
                {formatNumber(piSupply.total_supply)} π
              </p>
              <div className="grey-zone">
                <div className="mt-2">
                  <p className="neutral-color-5">{t('home.circulating')}</p>
                  <p className="medium-number">
                    {formatNumber(piSupply.circulating_supply)} π
                  </p>
                  <hr className="neutral-color-5" />
                  <p className="neutral-color-5">{t('home.locked')}</p>
                  <p className="medium-number">
                    {formatNumber(piSupply.total_locked)} π
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* CEX Balances */}
        <div className="col-md-3">
          <Card>
            <CardContent>
              <h5 className="card-title d-flex justify-content-between" style={{height:"16px"}}>
                <span>{t('home.cex_balance_total')}</span>
                <a href="/core-team-cex-statistics" className="btn btn-link p-0 text-decoration-none">
                  {t('home.view_all')} <i className="bi bi-arrow-right"></i>
                </a>
              </h5>
              <p className="strong-number">
                {formatNumber(totalCexBalance)} π
              </p>
              <div className="grey-zone">
                <div className="mt-2">
                  {cexBalances.map((exchange, idx) => (
                    <div key={idx}>
                      <p className="neutral-color-5">
                        <img src={exchange.logo} alt={exchange.name} className="cex-logo" width={20} />
                        {" "}
                        {exchange.name}
                      </p>
                      <p className="medium-number">
                        {formatNumber(exchange.balance)} π
                      </p>
                      {idx < cexBalances.length - 1 && (
                        <hr className="neutral-color-5" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Network Stats */}
        <div className="col-md-3">
          <Card>
            <CardContent>
              <h5 className="card-title">{t('home.network_stats')}</h5>
              <p className="strong-number">
                {tps} TPS
              </p>
              <div className="grey-zone">
                <div className="mt-2">
                  <p className="neutral-color-5">{t('home.blocks')}</p>
                  <p className="medium-number">
                    {formatNumber(latestBlock)}
                  </p>
                  <hr className="neutral-color-5" />
                  <p className="neutral-color-5">{t('home.tps')}</p>
                  <p className="medium-number">{tps}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      <div className="row g-3 mb-4 d-block d-md-none">
      {/* <AdUnit
                  className="67cc9f95aa72d3d47fe128c2"
                  width="300px"
                  height="100px"
                  centered={true}
                  containerStyle={{ width: "100%" }}
                /> */}
                
      </div>
      
      {/* Row 2: Latest Operations & Block Chart */}
      <div className="row g-3 mb-4">
        <div className="col-md-6" style={{ minHeight: "400px", borderBottom: "1px solid #e9ecef" }}>
          <TradingViewWidget/>
        </div>
        
        {/* Latest Transaction */}
        <div className="col-md-6">
          <Card className="none-radius">
            <CardContent>
              <h5 className="card-title">{t('home.latest_transactions')}</h5>
              <table className="table table-hover mt-3">
                <thead>
                  <tr>
                    <th>{t('home.signature')}</th>
                    <th>{t('home.time')}</th>
                    <th>{t('home.block')}</th>
                    <th>{t('home.ops')}</th>
                  </tr>
                </thead>
                <tbody>
                  {latestTransactions.map((tx, idx) => (
                    <tr key={idx}>
                      <td>
                        <Link to={`/tx/${tx.hash}`} style={{ textDecoration: "none" }}>
                          {tx.hash.slice(0, 6)}...{tx.hash.slice(-6)}
                        </Link>
                      </td>
                      <td>{timelib.timeAgo(tx.created_at, language)}</td>
                      <td>
                        <Link to={`/block/${tx.ledger}`} style={{ textDecoration: "none" }}>
                          {formatNumber(tx.ledger)}
                        </Link>
                      </td>
                      <td>{tx.operation_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      </div>
      
      <div className="row g-3"> 
        <div className="col-md-12">
          <Card className="none-radius">
            <CardContent>
              <h5 className="card-title">{t('home.latest_operations')}</h5>
              <Table hover responsive className="mt-3">
                <thead>
                  <tr>
                    <th>{t('home.source_account')}</th>
                    <th>{t('home.operation')}</th>
                    <th>{t('home.amount')}</th>
                    <th>{t('home.tx')}</th>
                    <th>{t('home.time')}</th>
                  </tr>
                </thead>
                <tbody>
                  {operations.map((op) => (
                    <tr key={op.id}>
                      <td>
                        <AccountLabel account={op.source_account} shorten={true}/>
                      </td>
                      <td><OperationText op={op} PiPrice={piPriceUSD}/></td>
                      <td style={{fontWeight:"500"}}>
                        {op.amount ? formatNumber(parseFloat(op.amount)) : ""}
                      </td>
                      <td>
                        <Link to={`/tx/${op.transaction_hash}`} className="text-decoration-none">
                          {op.transaction_hash.slice(0, 16)}...
                        </Link>
                        {" "}
                        <CopyIcon textToCopy={op.transaction_hash} />
                      </td>
                      <td>{timelib.timeAgo(op.created_at, language)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
